/**
 * Chat Message Content Component
 *
 * Renders AI chat messages with enhanced formatting for recommendations sections
 */

import React from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Divider from '@mui/material/Divider'
import { useTheme } from '@mui/material/styles'
import Icon from '@/src/@core/components/icon'
import { parseChatResponse } from '../utils/parseChatResponse'

interface ChatMessageContentProps {
  content: string
  isUser: boolean
}

const RecommendationBox: React.FC<{
  type: 'immediate' | 'preventive' | 'longterm' | 'monitoring'
  title: string
  items: string[]
}> = ({ type, title, items }) => {
  const theme = useTheme()

  const getBoxStyles = () => {
    switch (type) {
      case 'immediate':
        return {
          borderLeft: `4px solid ${theme.palette.error.main}`,
          bgcolor: `${theme.palette.error.main}08`,
          icon: 'tabler:alert-circle' as const,
          iconColor: theme.palette.error.main
        }
      case 'preventive':
        return {
          borderLeft: `4px solid ${theme.palette.warning.main}`,
          bgcolor: `${theme.palette.warning.main}08`,
          icon: 'tabler:shield-check' as const,
          iconColor: theme.palette.warning.main
        }
      case 'longterm':
        return {
          borderLeft: `4px solid ${theme.palette.info.main}`,
          bgcolor: `${theme.palette.info.main}08`,
          icon: 'tabler:calendar' as const,
          iconColor: theme.palette.info.main
        }
      case 'monitoring':
        return {
          borderLeft: `4px solid ${theme.palette.success.main}`,
          bgcolor: `${theme.palette.success.main}08`,
          icon: 'tabler:eye' as const,
          iconColor: theme.palette.success.main
        }
    }
  }

  const styles = getBoxStyles()

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 2,
        borderLeft: styles.borderLeft,
        bgcolor: styles.bgcolor,
        borderRadius: 1
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Icon icon={styles.icon} fontSize={20} style={{ color: styles.iconColor }} />
        <Typography variant='subtitle1' sx={{ fontWeight: 600, color: styles.iconColor }}>
          {title}
        </Typography>
      </Box>
      <List dense sx={{ pt: 0 }}>
        {items.map((item, idx) => (
          <ListItem key={idx} sx={{ py: 0.5, px: 0 }}>
            <ListItemIcon sx={{ minWidth: 24 }}>
              <Icon icon='tabler:circle-filled' fontSize={6} style={{ color: styles.iconColor }} />
            </ListItemIcon>
            <ListItemText
              primary={item}
              primaryTypographyProps={{
                variant: 'body2',
                sx: { lineHeight: 1.6 }
              }}
            />
          </ListItem>
        ))}
      </List>
    </Paper>
  )
}

export const ChatMessageContent: React.FC<ChatMessageContentProps> = ({ content, isUser }) => {
  const theme = useTheme()

  // For user messages, just render plain text
  if (isUser) {
    return (
      <Typography variant='body1' sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {content}
      </Typography>
    )
  }

  // Parse AI assistant messages
  const parsed = parseChatResponse(content)

  return (
    <Box>
      {/* Content before recommendations */}
      {parsed.contentBeforeRecommendations && (
        <Typography
          variant='body1'
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            mb: parsed.recommendations.length > 0 ? 3 : 0
          }}
        >
          {parsed.contentBeforeRecommendations}
        </Typography>
      )}

      {/* Recommendations section */}
      {parsed.recommendations.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 215, 4, 0.1)' : '#FFF9E6',
              border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 215, 4, 0.3)' : '#FFD204'}`,
              borderRadius: 2,
              mb: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Icon icon='tabler:bulb' fontSize={24} style={{ color: '#FFD204' }} />
              <Typography variant='h6' sx={{ fontWeight: 700, color: '#000' }}>
                Recommendations
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {parsed.recommendations.map((rec, idx) => (
              <RecommendationBox key={idx} type={rec.type} title={rec.title} items={rec.content} />
            ))}
          </Paper>
        </Box>
      )}

      {/* Content after recommendations */}
      {parsed.contentAfterRecommendations && (
        <Typography variant='body1' sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {parsed.contentAfterRecommendations}
        </Typography>
      )}

      {/* Fallback: if parsing didn't work, show original content */}
      {parsed.contentBeforeRecommendations === content && parsed.recommendations.length === 0 && (
        <Typography variant='body1' sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {content}
        </Typography>
      )}
    </Box>
  )
}
